import { useQuery } from '@tanstack/react-query';
import { woocommerceApi } from '@/lib/api/services/woocommerce';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { Order } from './useCommerce';

interface UseWooCommerceOrdersOptions {
    filters?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: string;
    };
}

export function useWooCommerceOrders({ filters }: UseWooCommerceOrdersOptions = {}) {
    const { selectedAccountId } = useBusinessAccountContext();
    const page = filters?.page || 1;
    const limit = filters?.limit || 20;

    return useQuery({
        queryKey: ['woocommerce-orders', selectedAccountId, filters],
        queryFn: async () => {
            if (!selectedAccountId) return { orders: [], total: 0, page, limit };

            try {
                const response = await woocommerceApi.getLocalOrders(selectedAccountId);

                if (!response.data) return { orders: [], total: 0, page, limit };

                // Map WooCommerce raw orders to unified Order interface
                const mappedOrders: Order[] = response.data.map((wooOrder: any) => {
                    let paymentStatus = 'pending';
                    if (wooOrder.status === 'completed' || wooOrder.status === 'processing') paymentStatus = 'paid';
                    else if (wooOrder.status === 'refunded') paymentStatus = 'refunded';
                    else if (wooOrder.status === 'cancelled') paymentStatus = 'cancelled';

                    return {
                        id: String(wooOrder.id),
                        order_number: String(wooOrder.order_number || wooOrder.id),
                        source: 'woocommerce',
                        customer_name: (wooOrder.billing?.first_name || '') + ' ' + (wooOrder.billing?.last_name || '') || 'Woo Customer',
                        customer_email: wooOrder.billing?.email,
                        customer_phone: wooOrder.billing?.phone,
                        total_amount: parseFloat(wooOrder.total),
                        currency: wooOrder.currency,
                        status: wooOrder.status || 'pending',
                        payment_status: paymentStatus,
                        created_at: wooOrder.date_created,
                        items_count: wooOrder.line_items?.length || 0,
                        source_id: String(wooOrder.id)
                    };
                });

                // Client-side pagination
                const startIndex = (page - 1) * limit;
                const endIndex = startIndex + limit;
                const paginatedOrders = mappedOrders.slice(startIndex, endIndex);

                return {
                    orders: paginatedOrders,
                    total: mappedOrders.length,
                    page,
                    limit
                };
            } catch (error) {
                return { orders: [], total: 0, page, limit };
            }
        },
        enabled: !!selectedAccountId,
    });
}
